import { toRelativeUrl } from '@okta/okta-auth-js';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { getCurrentUserRoles } from '@app/actions/auth';
import {
  LIVE_UPDATE_STRATEGY_SELECTED,
  SET_SOCKET_FAILURE,
} from '@app/constants/actions';
import { logoutConfig } from '@app/constants/okta';
import {
  getPreviousVirusScanningPermission,
  savePreviousVirusScanningPermission,
} from '@app/helpers/localStorage';
import isLoadedFromMobile, {
  isLoadedFromHiMarleyConnect,
  shouldUseNativeAuth,
} from '@app/helpers/platform';
import { resetZendeskWidget } from '@app/helpers/zendesk/zendesk-widget';
import globalMediaCacheFactory from '@app/hooks/global-file-cache';
import { useSafeAuth } from '@app/hooks/use-safe-auth';
import useTopicHandler from '@app/hooks/use-topic-handler';
import { clearStoredLocale } from '@app/i18n/stored-locale';
import { invalidateCasesQueries } from '@app/services/case/cases';
import {
  getWebSocketConnection,
  getWebSocketStatus,
  reconnectWebSocket,
} from '@app/services/websocket';
import {
  initNativeLifecycleBridge,
  destroyNativeLifecycleBridge,
} from '@app/services/websocket/native-lifecycle-bridge';
import {
  initWebLifecycleBridge,
  destroyWebLifecycleBridge,
} from '@app/services/websocket/web-lifecycle-bridge';
import { getNativeAuthBridge } from '@app/utils/native-auth-bridge';

// If the WebSocket was disconnected for longer than this, invalidate inbox
// CASES queries on reconnect as a safety net beyond the server-side topic
// replay. Shorter blips are handled correctly by the topic-timestamp
// catch-up; refetching on every flicker would put unnecessary REST load on
// the case query endpoint at scale.
const RECONNECT_REFETCH_GAP_MS = 30_000;

declare global {
  interface Window {
    sendTokens?: (arg: string) => void;
  }
}

export const useAuthenticationRedirect = () => {
  const navigate = useNavigate();
  const { oktaAuth, authState, isReady } = useSafeAuth();

  useEffect(() => {
    if (!isReady) {
      return;
    }

    const handleAuth = async () => {
      if (!authState?.isAuthenticated) {
        if (shouldUseNativeAuth()) {
          navigate('/login');
        } else {
          const originalUri = toRelativeUrl(
            window.location.href,
            window.location.origin,
          );
          oktaAuth.setOriginalUri(originalUri);
          if (isLoadedFromMobile() || isLoadedFromHiMarleyConnect()) {
            oktaAuth.signInWithRedirect({
              scopes: ['openid', 'profile', 'email', 'offline_access'],
            });
          } else {
            // Simple navigation - native app will handle its own logic
            navigate('/login');
          }
        }
      }
    };
    handleAuth();
  }, [oktaAuth, navigate, isReady, authState?.isAuthenticated]);

  return { oktaAuth, authState };
};

export const useAuthInitialization = () => {
  const dispatch = useDispatch();
  const { oktaAuth, authState, isReady } = useSafeAuth();

  useEffect(() => {
    if (!isReady || !authState?.isAuthenticated) {
      return;
    }

    const initializeAuth = async () => {
      // Get user ID from auth state
      const oktaUserId = authState?.idToken?.claims?.preferred_username;
      if (oktaUserId) {
        // If on mobile, send accessToken and idToken to native to register push notifications
        if (isLoadedFromMobile() && !shouldUseNativeAuth()) {
          const accessToken = oktaAuth.getAccessToken();
          const idToken = oktaAuth.getIdToken();
          const authData = JSON.stringify({
            accessToken,
            idToken,
          });
          if (typeof window.sendTokens === 'function') {
            // Send the data to the reactnative app via sendTokens
            window.sendTokens(authData);
          } else {
            // If running outside WebView, log an error or handle gracefully
            console.error(
              'Error: window.sendTokens is not available. Are you running in WebView?',
            );
          }
        }
      }
    };
    initializeAuth();
  }, [
    dispatch,
    oktaAuth,
    isReady,
    authState?.isAuthenticated,
    authState?.idToken?.claims?.preferred_username,
  ]);
};

export const useClearMediaCache = (authId: string | undefined) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // invalidate media cache when FEATURE_FLAG_VIRUS_SCANNING is changed
    const clearMediaCache = async () => {
      const globalFileCache = await globalMediaCacheFactory();
      await globalFileCache?.deleteAllItems();
    };

    if (authId) {
      dispatch(getCurrentUserRoles(authId));
      const previousVirusScanningPermission =
        getPreviousVirusScanningPermission(authId);
      if (
        previousVirusScanningPermission !==
        process.env.FEATURE_FLAG_VIRUS_SCANNING
      ) {
        clearMediaCache();
      }
      savePreviousVirusScanningPermission(
        authId,
        process.env.FEATURE_FLAG_VIRUS_SCANNING,
      );
    }
  }, [authId, dispatch]);
};

export const useLogoutHandler = (logout: boolean | undefined) => {
  const { oktaAuth } = useSafeAuth();

  useEffect(() => {
    if (!logout) return;

    const handleLogout = async () => {
      clearStoredLocale();
      await resetZendeskWidget();

      // Letting the extension know we're logging out
      window.parent.postMessage(
        { action: 'userLoggedOut', source: 'webapp' },
        window.location.origin,
      );
      window.postMessage(
        { action: 'userLoggedOut', source: 'webapp' },
        window.location.origin,
      );

      if (shouldUseNativeAuth()) {
        // For native auth: use the bridge to sign out
        try {
          const nativeBridge = getNativeAuthBridge();
          await nativeBridge.signOut();
        } catch (error) {
          console.error('Native auth logout failed:', error);
        }
      } else {
        // For web auth: use standard oktaAuth client
        await oktaAuth.signOut(logoutConfig);
      }
    };

    handleLogout();
  }, [oktaAuth, logout]);
};

export const useWebSocketConnection = () => {
  const dispatch = useDispatch();
  // this isn't really a 'topic' handler - its a message handler
  const wsMessageHandler = useTopicHandler();
  const wsMessageHandlerRef = useRef(wsMessageHandler);
  // Tracks when the socket first dropped during the current outage so we can
  // measure the gap on reconnect. Reset to null after each successful reconnect.
  const disconnectedAtRef = useRef<number | null>(null);

  // Keep ref updated with latest handler
  useEffect(() => {
    wsMessageHandlerRef.current = wsMessageHandler;
  }, [wsMessageHandler]);

  useEffect(() => {
    const ws = getWebSocketConnection();
    ws.connect();

    // Initialize the native lifecycle bridge so the React Native shell can
    // signal foreground/background transitions and trigger reconnection.
    initNativeLifecycleBridge({ getWebSocketStatus, reconnectWebSocket });
    // Web-only equivalent: reconnect when the tab regains visibility or the
    // browser reports online — covers cases the mobile bridge can't see.
    initWebLifecycleBridge({ reconnectWebSocket });

    // TODO: ensure error handling is correct or enhance
    const wsConnectionTimeout = setTimeout(() => {
      dispatch({
        type: SET_SOCKET_FAILURE,
      });
    }, 10000);

    const handleMessage = (e: Event) => {
      if (e instanceof CustomEvent && 'detail' in e) {
        const customEvent = e as CustomEvent<{
          payload?: Array<{ name: string; payload: unknown; userId: string }>;
        }>;
        wsMessageHandlerRef.current(customEvent);
      }
    };

    const handleConnectionRetry = (e: Event) => {
      // First retry of an outage marks the gap start. Subsequent retries
      // within the same outage keep the original timestamp so a long backoff
      // chain is still measured from the moment the socket actually dropped.
      if (disconnectedAtRef.current !== null) {
        return;
      }
      let gapStart = Date.now();
      if (
        e instanceof CustomEvent &&
        e.detail &&
        typeof e.detail === 'object'
      ) {
        const { gapStartTimestamp } = e.detail as {
          gapStartTimestamp?: unknown;
        };
        if (
          typeof gapStartTimestamp === 'number' &&
          Number.isFinite(gapStartTimestamp)
        ) {
          gapStart = gapStartTimestamp;
        }
      }
      disconnectedAtRef.current = gapStart;
    };

    const handleConnected = () => {
      clearTimeout(wsConnectionTimeout);
      ws.addEventListener('message', handleMessage);
      dispatch({
        type: LIVE_UPDATE_STRATEGY_SELECTED,
      });
      // Safety net: if the WS was down long enough that we don't trust the
      // server-side topic replay to have caught everything, force a fresh
      // pull of the inbox so missed cases/messages still appear at the top.
      const disconnectedAt = disconnectedAtRef.current;
      if (
        disconnectedAt !== null &&
        Date.now() - disconnectedAt > RECONNECT_REFETCH_GAP_MS
      ) {
        invalidateCasesQueries();
      }
      disconnectedAtRef.current = null;
    };

    ws.addEventListener('connection-retry', handleConnectionRetry);
    ws.addEventListener('connected', handleConnected);

    // Cleanup function: destroy bridges first so no reconnect runs during teardown, then remove listeners
    return () => {
      destroyNativeLifecycleBridge();
      destroyWebLifecycleBridge();
      clearTimeout(wsConnectionTimeout);
      ws.removeEventListener('connection-retry', handleConnectionRetry);
      ws.removeEventListener('connected', handleConnected);
      ws.removeEventListener('message', handleMessage);
    };
  }, [dispatch]);
};
